import type { PdfImportExtracted } from "../components/ImportInvoicesDropzone";

export type LineTipo = "servicio" | "gasto_no_suplido" | "suplido";

export interface EditableConceptLine {
  id: string;
  tipo: LineTipo;
  descripcion: string;
  importe: number;
  codigo?: string;
  cantidad?: number;
  unidad?: string;
  gastoTipo?: string;
}

export const formatConfidencePercent = (confidence?: number | null): number => {
  if (confidence == null || confidence === undefined) return 0;
  let n = Number(confidence);
  if (!Number.isFinite(n)) return 0;
  if (n > 0 && n <= 1) n = Math.round(n * 100);
  return Math.min(100, Math.max(0, Math.round(n)));
};

export const extractedToLines = (ext: PdfImportExtracted): EditableConceptLine[] => {
  const lines: EditableConceptLine[] = [];
  const servicios = ext.conceptos_servicio?.length
    ? ext.conceptos_servicio
    : ext.conceptos || [];

  servicios.forEach((c, i) => {
    lines.push({
      id: `svc-${i}-${Date.now()}`,
      tipo: "servicio",
      descripcion: c.descripcion || "",
      importe: Number(c.importe || 0),
      codigo: c.codigo || undefined,
      cantidad: c.cantidad ?? undefined,
      unidad: c.unidad || undefined,
    });
  });

  (ext.gastos_no_suplidos || []).forEach((g, i) => {
    lines.push({
      id: `gns-${i}-${Date.now()}`,
      tipo: "gasto_no_suplido",
      descripcion: g.descripcion || "",
      importe: Number(g.importe || 0),
      gastoTipo: g.tipo || "otros",
    });
  });

  (ext.suplidos || []).forEach((s, i) => {
    lines.push({
      id: `sup-${i}-${Date.now()}`,
      tipo: "suplido",
      descripcion: s.descripcion || "",
      importe: Number(s.importe || 0),
    });
  });

  return lines;
};

export const linesToExtracted = (
  lines: EditableConceptLine[],
  base: PdfImportExtracted
): PdfImportExtracted => {
  const conceptos_servicio = lines
    .filter((l) => l.tipo === "servicio")
    .map((l) => ({
      descripcion: l.descripcion,
      importe: l.importe,
      codigo: l.codigo || null,
      cantidad: l.cantidad ?? null,
      unidad: l.unidad || null,
    }));

  const gastos_no_suplidos = lines
    .filter((l) => l.tipo === "gasto_no_suplido")
    .map((l) => ({
      descripcion: l.descripcion,
      importe: l.importe,
      tipo: l.gastoTipo || "otros",
    }));

  const suplidos = lines
    .filter((l) => l.tipo === "suplido")
    .map((l) => ({
      descripcion: l.descripcion,
      importe: l.importe,
    }));

  const baseTrabajo = conceptos_servicio.reduce((s, c) => s + Number(c.importe || 0), 0);
  const baseGastos = gastos_no_suplidos.reduce((s, g) => s + Number(g.importe || 0), 0);
  const baseImponible =
    base.base_imponible != null && base.base_imponible > 0
      ? base.base_imponible
      : Math.round((baseTrabajo + baseGastos) * 100) / 100;

  return {
    ...base,
    conceptos_servicio,
    conceptos: conceptos_servicio,
    gastos_no_suplidos,
    suplidos,
    base_trabajo: baseTrabajo,
    base_gastos_no_suplidos: baseGastos,
    base_imponible: baseImponible,
  };
};

export interface FiscalPreview {
  baseTrabajo: number;
  baseGastos: number;
  baseImponible: number;
  ivaPct: number;
  ivaImporte: number;
  irpfPct: number;
  irpfImporte: number;
  suplidos: number;
  total: number;
}

export const computeFiscalPreview = (ext: PdfImportExtracted): FiscalPreview => {
  const lines = extractedToLines(ext);
  const baseTrabajo = lines
    .filter((l) => l.tipo === "servicio")
    .reduce((s, l) => s + l.importe, 0);
  const baseGastos = lines
    .filter((l) => l.tipo === "gasto_no_suplido")
    .reduce((s, l) => s + l.importe, 0);
  const suplidos = lines
    .filter((l) => l.tipo === "suplido")
    .reduce((s, l) => s + l.importe, 0);
  const baseImponible = Number(ext.base_imponible ?? baseTrabajo + baseGastos);
  const ivaPct = Number(ext.iva_porcentaje ?? 21);
  const irpfPct = Number(ext.irpf_porcentaje ?? 15);
  const ivaImporte = Number(ext.iva_importe ?? (baseImponible * ivaPct) / 100);
  const irpfImporte = Number(ext.irpf_importe ?? (baseImponible * irpfPct) / 100);
  const total = Number(ext.total ?? baseImponible + ivaImporte - irpfImporte + suplidos);

  return {
    baseTrabajo,
    baseGastos,
    baseImponible,
    ivaPct,
    ivaImporte,
    irpfPct,
    irpfImporte,
    suplidos,
    total,
  };
};
